import React from 'react';
import { Row, Col } from 'react-styled-flexboxgrid';
import {
  Wrap,
  Typography,
  Icon,
  ChartBarsMeter,
  Input,
  Button,
  Popup,
  Notifications,
  LocationDropdown,
  ErrorDisplay,
  SkeletonLoader,
} from '../../components/index';
import { useFormik } from 'formik';
import Styled from './PageMeterRead.style';
import {
  prettifyIfNumber,
  scrollToTop,
  useResponsivity,
} from '../../helpers/helpers';
import * as Yup from 'yup';
import store from 'store2';
import * as _ from 'lodash';
import { useNavigate } from 'react-router-dom';

interface PageMeterReadProps {
  listLocation: any;
  setMeta: Function;
  getMeterRead: Function;
  submitMeterRead: Function;
  meterRead: any;
}

export const PageMeterRead: React.FC<PageMeterReadProps> = (props) => {
  const { listLocation, setMeta, submitMeterRead, getMeterRead, meterRead } =
    props;
  const isMobile = useResponsivity();
  const errorSubmitMeter = _.get(meterRead, 'submitMeterRead.status.error');
  const meterReadDataSubmit = _.get(meterRead, 'submitMeterRead.data.message');
  const { error, done, loading } = _.get(meterRead, 'getMeterRead.status');
  const [popup, setPopup] = React.useState(false);
  const initialPropertyId = Number(store.get('property')) || 0;
  const [propertyId, setPropertyId] = React.useState<number>(initialPropertyId);
  const dataLocation = _.get(listLocation, 'data.data');
  const dataLength = _.get(dataLocation, 'length');
  const navigate = useNavigate();
  const dataMeterRead = _.get(meterRead, 'getMeterRead.data.data');
  const MeterReadSchema = Yup.object().shape({
    meterRead: Yup.string()
      .matches(/^\d+$/, 'The field should have digits only')
      .required('Required'),
  });

  React.useEffect(() => {
    getMeterRead(initialPropertyId);
  }, [initialPropertyId]);

  const formik = useFormik({
    initialValues: {
      meterRead: '',
      propertyId: '',
    },
    validationSchema: MeterReadSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await submitMeterRead(propertyId, values.meterRead);

        // Only reset after successful submission
        getMeterRead(propertyId);
        resetForm();
        scrollToTop();
      } catch (error) {
        console.error('Failed to submit meter reading:', error);
        setPopup(false);
      } finally {
        setSubmitting(false);
        setPopup(false);
      }
    },
  });
  const renderMapOptions =
    dataLocation &&
    dataLocation.map((el) => ({
      value: el.id,
      label: el.property_name,
    }));

  const chartData = {
    dataSets: {
      use: {
        data:
          dataMeterRead &&
          dataMeterRead.map((item) => {
            const date = new Date(item.date);

            return {
              reading: item.meterRead,
              value: item.litersUsedInPeriod,
              label: date.toLocaleDateString('en-GB', {
                month: 'short',
              }),
            };
          }),
        yLabel: 'portal.my_use.chart_selector_option_litre',
      },
    },
  };

  const data = chartData.dataSets.use.data;
  if (error) {
    return (
      <Wrap>
        <ErrorDisplay />
      </Wrap>
    );
  }
  if (loading && !done) {
    return (
      <Wrap>
        <SkeletonLoader />
      </Wrap>
    );
  }
  const renderItems = () => {
    return (
      data &&
      data
        .slice()
        .reverse()
        .map((el, index) => {
          const previousValue = data[index - 1]?.value;

          const isHigher =
            index > 0 &&
            previousValue !== undefined &&
            el.value > previousValue;

          const isLower =
            index > 0 &&
            previousValue !== undefined &&
            el.value < previousValue;

          const arrowColor =
            index === 0
              ? '#21AAE8'
              : isHigher
                ? '#f1b91a'
                : isLower
                  ? '#4BAE50'
                  : '#21AAE8';

          const arrowRotation =
            index === 0
              ? '0deg'
              : isHigher
                ? '-45deg'
                : isLower
                  ? '45deg'
                  : '0deg';
          const year = new Date().getFullYear();

          return (
            <Styled.BoxContentItem key={index}>
              <div className="date">
                {el.label} {year}
              </div>
              <div className="title-container">
                <div className="subTitle">
                  Reading: {prettifyIfNumber(el.reading)} m<sup>3</sup>
                </div>
                <div className="title">
                  Litres used: {prettifyIfNumber(el.value)} L
                </div>
              </div>

              <div className="arrow">
                <Styled.ArrowWrap color={arrowColor}>
                  <Icon
                    icon="arrow"
                    size={30}
                    colorPrimary="black"
                    colorSecondary="black"
                    style={{ transform: `rotate(${arrowRotation})` }}
                  />
                </Styled.ArrowWrap>
              </div>
            </Styled.BoxContentItem>
          );
        })
    );
  };
  return (
    <Wrap>
      <Row style={{ marginBottom: '32px' }}>
        {dataLength <= 1 ? null : (
          <Col md={12} xs={12} sm={12}>
            <Styled.DropdownWrap>
              <LocationDropdown
                value={String(propertyId)}
                onChange={(e) => {
                  const id = Number(e.target.value);

                  setPropertyId(id);
                  store('property', id);
                  setMeta(id);
                  getMeterRead(id);
                }}
                options={renderMapOptions}
              />
            </Styled.DropdownWrap>
          </Col>
        )}

        <Col md={12} sm={12} xs={12}>
          <Styled.Box>
            <Styled.BoxHeader>
              <Icon icon="meter" size={100} />
              <Typography
                size="1"
                style={{
                  fontSize: '30px',
                  width: '600px',
                  textAlign: 'center',
                  margin: '16px 0px 32px 0px',
                }}
                text="Tell us your meter reading & track your water-saving progress"
              />

              <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Input
                    id="meterRead"
                    name="meterRead"
                    value={formik.values.meterRead}
                    type="text"
                    placeholder="Water meter reading"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      height: '50px',
                      borderRadius: '8px 0px 0px 8px',
                      width: '300px',
                    }}
                    error={
                      formik.errors.meterRead && formik.touched.meterRead
                        ? formik.errors.meterRead
                        : undefined
                    }
                  />
                  <Button
                    style={{
                      height: '50px',
                      borderRadius: '0px 8px 8px 0px',
                    }}
                    appearance="REVERSE"
                    disabled={formik.isSubmitting}
                    onClick={() => {
                      if (formik.values.meterRead) {
                        setPopup(!popup);
                      }
                    }}
                  >
                    Submit
                    <Icon
                      icon="arrow"
                      size={20}
                      colorPrimary="white"
                      colorSecondary="white"
                      style={{ marginLeft: '16px' }}
                    />
                  </Button>
                  <Popup
                    isDesktop={!isMobile}
                    open={popup}
                    handleClick={() => {
                      setPopup(!popup);
                    }}
                  >
                    <Typography size="3" text="Are you sure?" />
                    <Typography
                      size="p"
                      text={`Meter read: ${formik.values.meterRead}`}
                    />

                    <Button
                      style={{
                        height: '50px',
                        borderRadius: '8px',
                      }}
                      appearance="REVERSE"
                      disabled={formik.isSubmitting}
                      type="submit"
                    >
                      Submit
                      <Icon
                        icon="arrow"
                        size={20}
                        colorPrimary="white"
                        colorSecondary="white"
                        style={{ marginLeft: '16px' }}
                      />
                    </Button>
                  </Popup>
                </div>
                {meterReadDataSubmit && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Notifications type="SUCCESS">
                      {meterReadDataSubmit}
                    </Notifications>
                  </div>
                )}
                {errorSubmitMeter && (
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Notifications type="ERROR">
                      {errorSubmitMeter}
                    </Notifications>
                  </div>
                )}
              </form>
              <div
                style={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
                onClick={() => navigate('/leaderboard')}
              >
                View leaderboard
              </div>
            </Styled.BoxHeader>
            {dataMeterRead && dataMeterRead.length > 1 ? (
              <Styled.BoxContent>{renderItems()}</Styled.BoxContent>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '16px',
                }}
              >
                <Typography
                  size="3"
                  text="You have no meter reads, please submit one."
                />
              </div>
            )}
            {dataMeterRead && dataMeterRead.length > 1 && (
              <Styled.BoxFooter>
                <ChartBarsMeter
                  id={23}
                  title="2026 Meter reads"
                  chartData={chartData}
                />
              </Styled.BoxFooter>
            )}
          </Styled.Box>
        </Col>
      </Row>
    </Wrap>
  );
};

export default PageMeterRead;
